import { useCallback } from "react";

import type {
	Beds24RoomTypeMappingItem,
	InputMaybe,
	SiteControllerSettingBeds24PriceAndStockDataSync_SettingPageMutation,
} from "@/lib/gql/__generated__/graphql";

import type { ForAutoGenerator } from "@/components/features/_model/types/auto-generator-for-view-model";

import { useSyncMasterDataBeds24 } from "../../../repository/mutation/master-data-sync-beds24/master-data-sync-beds24";
import { useSyncPriceAndStockDataBeds24 } from "../../../repository/mutation/price-and-stock-data-sync-beds24/price-and-stock-data-sync-beds24";

import type { SiteControllerSettingBaseFormModelOnSetViewModelType } from "../../../../base/use-case/use-create-model/use-create-model";
import type { SiteControllerSettingBeds24FormModel } from "../../../type";
// eslint-disable-next-line import/no-restricted-paths
import type { FetchResult } from "@apollo/client";

type Props = {
	viewModel: SiteControllerSettingBeds24FormModel["ViewModel"];
	onSetViewModel: SiteControllerSettingBaseFormModelOnSetViewModelType;
};

export type SiteControllerSettingBeds24FormModelMethodsType = {
	siteControllerSettingBeds24FormModelMethods: {
		onSyncPriceAndStockData: () => Promise<
			FetchResult<SiteControllerSettingBeds24PriceAndStockDataSync_SettingPageMutation>
		>;

		onSyncMasterData: () => Promise<void>;
		canSyncMasterData: boolean;

		onSetBeds24AuthenticationApiKey: (value: string) => void;
		onSetBeds24AuthenticationPropKey: (value: string) => void;
		onSetSetUpRoomPriceByChillnn: (value: boolean) => void;
		onSetBeds24RoomTypeMapping: ({ roomID, beds24RoomID }: { roomID: string; beds24RoomID: string }) => void;
	};
};

type UseSiteControllerSettingBeds24FormModelMethodsType = ({
	viewModel,
	onSetViewModel,
}: Props) => SiteControllerSettingBeds24FormModelMethodsType;

export const useSiteControllerSettingBeds24FormModelMethods: UseSiteControllerSettingBeds24FormModelMethodsType = ({
	viewModel,
	onSetViewModel,
}) => {
	const { onSyncPriceAndStockData } = useSyncPriceAndStockDataBeds24({ hotelID: viewModel.hotelID });

	const onSetBeds24AuthenticationApiKey = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "beds24AuthenticationApiKey",
					value: {
						isEdited: true,
						value,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetBeds24AuthenticationPropKey = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "beds24AuthenticationPropKey",
					value: {
						isEdited: true,
						value,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetSetUpRoomPriceByChillnn = useCallback(
		(value: boolean) => {
			onSetViewModel([
				{
					key: "setUpRoomPriceByChillnn",
					value: {
						isEdited: true,
						value,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetBeds24RoomTypeMapping = useCallback(
		({ roomID, beds24RoomID }: { roomID: string; beds24RoomID: string }) => {
			const { beds24RoomTypeMapping } = viewModel;
			const newValue: ForAutoGenerator<Beds24RoomTypeMappingItem, "">[] = beds24RoomTypeMapping.value.map(
				(item) => {
					if (item.beds24roomID?.value === beds24RoomID) {
						return {
							...item,
							roomID: {
								isEdited: true,
								value: roomID,
							},
						};
					}
					return item;
				},
			);

			onSetViewModel([
				{
					key: "beds24RoomTypeMapping",
					value: {
						isEdited: true,
						hasError: false,
						value: newValue,
					},
				},
			]);
		},
		[onSetViewModel, viewModel],
	);

	const onReplaceBeds24RoomTypeMapping = useCallback(
		(
			values: {
				beds24roomID: string;
				name: string;
				roomID?: InputMaybe<string> | undefined;
				beds24AuthenticationPropKey?: InputMaybe<string> | undefined;
			}[],
		) => {
			const newValues: ForAutoGenerator<Beds24RoomTypeMappingItem, "">[] = values.map((value) => {
				return {
					beds24roomID: {
						isEdited: false,
						value: value.beds24roomID,
					},
					name: {
						isEdited: false,
						value: value.name,
					},
					roomID: {
						isEdited: false,
						value: value.roomID,
					},
					beds24AuthenticationPropKey: {
						isEdited: false,
						value: value.beds24AuthenticationPropKey,
					},
				} satisfies SiteControllerSettingBeds24FormModel["ViewModel"]["beds24RoomTypeMapping"]["value"][number];
			});

			onSetViewModel([
				{
					key: "beds24RoomTypeMapping",
					value: {
						isEdited: true,
						hasError: false,
						value: newValues,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const { onSyncMasterData: _onSyncMasterData } = useSyncMasterDataBeds24({ viewModel });
	const canSyncMasterData =
		!!viewModel.beds24AuthenticationApiKey.value && !!viewModel.beds24AuthenticationPropKey.value;

	const onSyncMasterData = useCallback(async () => {
		if (!canSyncMasterData) return;
		const { data, errors } = await _onSyncMasterData();

		if (!errors?.length && data?.siteControllerSettingBeds24MasterDataSync.siteControllerSetting) {
			const { beds24RoomTypeMapping } = data.siteControllerSettingBeds24MasterDataSync.siteControllerSetting;

			if (beds24RoomTypeMapping) {
				onReplaceBeds24RoomTypeMapping(beds24RoomTypeMapping);
			}
		}
	}, [_onSyncMasterData, canSyncMasterData, onReplaceBeds24RoomTypeMapping]);

	return {
		siteControllerSettingBeds24FormModelMethods: {
			onSyncPriceAndStockData,

			onSyncMasterData,
			canSyncMasterData,

			onSetBeds24AuthenticationApiKey, //
			onSetBeds24AuthenticationPropKey,
			onSetSetUpRoomPriceByChillnn,
			onSetBeds24RoomTypeMapping,
		},
	};
};
